import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Badge, Alert, Form, Row, Col, Button, InputGroup } from 'react-bootstrap';
import { getAuthenticatedAxios } from '../CheckOutProcess/axiosConfig';
import { useAuth } from '../AuthContext/AuthContext';
import { BASE_URL } from '../../Constants/constants';
import { formatDate, formatDateOnly } from '../CheckOutProcess/Dateutils';
import { useTranslation } from 'react-i18next';

const CompletedCheckoutsList = () => {
    const { t } = useTranslation('completedCheckoutsList');
    const { currentUser } = useAuth();
    const [completedCheckouts, setCompletedCheckouts] = useState([]);
    const [filteredCheckouts, setFilteredCheckouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'checkout_completion_date', direction: 'desc' });

    // Fetch completed checkouts
    const fetchCompletedCheckouts = async () => {
        try {
            setLoading(true);
            const axios = getAuthenticatedAxios();
            const response = await axios.get(`${BASE_URL}/checkout/completed-checkouts`);

            const checkouts = Array.isArray(response.data) ? response.data : [];
            setCompletedCheckouts(checkouts);
            setFilteredCheckouts(checkouts);
            setError(null);
        } catch (err) {
            console.error(t('completedCheckoutsList.errors.fetch'), err);
            setError(
                err.response?.data?.detail ||
                t('completedCheckoutsList.errors.fetchFailed')
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!currentUser) {
            setError(t('completedCheckoutsList.errors.authRequired'));
            setLoading(false);
            return;
        }

        fetchCompletedCheckouts();
    }, [currentUser]);

    // Filter and search functionality
    useEffect(() => {
        let filtered = [...completedCheckouts];

        // Search filter
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(checkout =>
                checkout.first_name?.toLowerCase().includes(search) ||
                checkout.last_name?.toLowerCase().includes(search) ||
                checkout.email?.toLowerCase().includes(search) ||
                checkout.section?.toLowerCase().includes(search)
            );
        }

        // Date range filter
        if (filterDateFrom) {
            filtered = filtered.filter(checkout => {
                const checkoutDate = new Date(checkout.checkout_completion_date);
                const fromDate = new Date(filterDateFrom);
                return checkoutDate >= fromDate;
            });
        }

        if (filterDateTo) {
            filtered = filtered.filter(checkout => {
                const checkoutDate = new Date(checkout.checkout_completion_date);
                const toDate = new Date(filterDateTo);
                toDate.setHours(23, 59, 59, 999); // Include the entire day
                return checkoutDate <= toDate;
            });
        }

        setFilteredCheckouts(filtered);
    }, [searchTerm, filterDateFrom, filterDateTo, completedCheckouts]);

    // Sorting functionality
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }

        const sorted = [...filteredCheckouts].sort((a, b) => {
            let aValue = a[key];
            let bValue = b[key];

            // Handle dates
            if (key.includes('date')) {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            // Handle strings
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });

        setFilteredCheckouts(sorted);
        setSortConfig({ key, direction });
    };

    // Export to CSV
    const exportToCSV = () => {
        const headers = [
            t('completedCheckoutsList.table.csvHeaders.firstName'),
            t('completedCheckoutsList.table.csvHeaders.lastName'),
            t('completedCheckoutsList.table.csvHeaders.email'),
            t('completedCheckoutsList.table.csvHeaders.section'),
            t('completedCheckoutsList.table.csvHeaders.checkoutStarted'),
            t('completedCheckoutsList.table.csvHeaders.ictCheckout'),
            t('completedCheckoutsList.table.csvHeaders.securityCheckout'),
            t('completedCheckoutsList.table.csvHeaders.hrCheckout'),
            t('completedCheckoutsList.table.csvHeaders.completedDate')
        ];

        const csvData = filteredCheckouts.map(checkout => [
            checkout.first_name || '',
            checkout.last_name || '',
            checkout.email || '',
            checkout.section || '',
            checkout.created_at ? formatDate(checkout.created_at) : '',
            checkout.ict_checkout_date ? formatDate(checkout.ict_checkout_date) : '',
            checkout.security_checkout_date ? formatDate(checkout.security_checkout_date) : '',
            checkout.hr_checkout_date ? formatDate(checkout.hr_checkout_date) : '',
            checkout.checkout_completion_date ? formatDate(checkout.checkout_completion_date) : ''
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `completed_checkouts_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Clear filters
    const clearFilters = () => {
        setSearchTerm('');
        setFilterDateFrom('');
        setFilterDateTo('');
    };

    // Get sort icon
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '⇅';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    if (loading) {
        return (
            <Container className="mt-4">
                <Alert variant="info">
                    <div className="d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">{t('completedCheckoutsList.loading')}</span>
                        </div>
                        {t('completedCheckoutsList.loading')}
                    </div>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h2>
                        <i className="bi bi-check-circle me-2"></i>
                        {t('completedCheckoutsList.title')}
                    </h2>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-primary"
                            onClick={fetchCompletedCheckouts}
                            size="sm"
                        >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            {t('completedCheckoutsList.buttons.refresh')}
                        </Button>
                        <Button
                            variant="success"
                            onClick={exportToCSV}
                            size="sm"
                            disabled={filteredCheckouts.length === 0}
                        >
                            <i className="bi bi-download me-1"></i>
                            {t('completedCheckoutsList.buttons.exportCSV')}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {error && (
                        <Alert
                            variant="danger"
                            onClose={() => setError(null)}
                            dismissible
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Filters Section */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">
                                <i className="bi bi-funnel me-2"></i>
                                {t('completedCheckoutsList.filters.title')}
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('completedCheckoutsList.filters.search')}</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <i className="bi bi-search"></i>
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder={t('completedCheckoutsList.filters.searchPlaceholder')}
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('completedCheckoutsList.filters.fromDate')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={filterDateFrom}
                                            onChange={(e) => setFilterDateFrom(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('completedCheckoutsList.filters.toDate')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={filterDateTo}
                                            onChange={(e) => setFilterDateTo(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2} className="d-flex align-items-end">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={clearFilters}
                                        className="mb-3 w-100"
                                    >
                                        {t('completedCheckoutsList.buttons.clearFilters')}
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Summary Statistics */}
                    <Row className="mb-3">
                        <Col md={3}>
                            <Card bg="primary" text="white">
                                <Card.Body>
                                    <h6>{t('completedCheckoutsList.stats.totalCompleted')}</h6>
                                    <h3>{completedCheckouts.length}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card bg="info" text="white">
                                <Card.Body>
                                    <h6>{t('completedCheckoutsList.stats.showing')}</h6>
                                    <h3>{filteredCheckouts.length}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card bg="success" text="white">
                                <Card.Body>
                                    <h6>{t('completedCheckoutsList.stats.thisMonth')}</h6>
                                    <h3>
                                        {completedCheckouts.filter(checkout => {
                                            const checkoutDate = new Date(checkout.checkout_completion_date);
                                            const now = new Date();
                                            return checkoutDate.getMonth() === now.getMonth() &&
                                                checkoutDate.getFullYear() === now.getFullYear();
                                        }).length}
                                    </h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card bg="warning" text="white">
                                <Card.Body>
                                    <h6>{t('completedCheckoutsList.stats.thisWeek')}</h6>
                                    <h3>
                                        {completedCheckouts.filter(checkout => {
                                            const checkoutDate = new Date(checkout.checkout_completion_date);
                                            const now = new Date();
                                            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                                            return checkoutDate >= weekAgo;
                                        }).length}
                                    </h3>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Checkouts Table */}
                    {filteredCheckouts.length === 0 ? (
                        <Alert variant="info">
                            <i className="bi bi-info-circle me-2"></i>
                            {t('completedCheckoutsList.table.noResults')}
                        </Alert>
                    ) : (
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead>
                                <tr>
                                    <th onClick={() => handleSort('first_name')} style={{ cursor: 'pointer' }}>
                                        {t('completedCheckoutsList.table.headers.name')} {getSortIcon('first_name')}
                                    </th>
                                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                                        {t('completedCheckoutsList.table.headers.email')} {getSortIcon('email')}
                                    </th>
                                    <th onClick={() => handleSort('section')} style={{ cursor: 'pointer' }}>
                                        {t('completedCheckoutsList.table.headers.section')} {getSortIcon('section')}
                                    </th>
                                    <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                                        {t('completedCheckoutsList.table.headers.started')} {getSortIcon('created_at')}
                                    </th>
                                    <th onClick={() => handleSort('ict_checkout_date')} style={{ cursor: 'pointer' }}>
                                        {t('completedCheckoutsList.table.headers.ict')} {getSortIcon('ict_checkout_date')}
                                    </th>
                                    <th onClick={() => handleSort('security_checkout_date')} style={{ cursor: 'pointer' }}>
                                        {t('completedCheckoutsList.table.headers.security')} {getSortIcon('security_checkout_date')}
                                    </th>
                                    <th onClick={() => handleSort('hr_checkout_date')} style={{ cursor: 'pointer' }}>
                                        {t('completedCheckoutsList.table.headers.hr')} {getSortIcon('hr_checkout_date')}
                                    </th>
                                    <th onClick={() => handleSort('checkout_completion_date')} style={{ cursor: 'pointer' }}>
                                        {t('completedCheckoutsList.table.headers.completed')} {getSortIcon('checkout_completion_date')}
                                    </th>
                                    <th>{t('completedCheckoutsList.table.headers.duration')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredCheckouts.map((checkout) => {
                                    // Calculate duration from start to completion
                                    const startDate = new Date(checkout.created_at);
                                    const endDate = new Date(checkout.checkout_completion_date);
                                    const durationDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

                                    return (
                                        <tr key={checkout.user_id}>
                                            <td>
                                                <strong>{checkout.first_name} {checkout.last_name}</strong>
                                            </td>
                                            <td>
                                                <small>{checkout.email || t('completedCheckoutsList.table.notAvailable')}</small>
                                            </td>
                                            <td>
                                                <Badge bg="secondary">{checkout.section || t('completedCheckoutsList.table.notAvailable')}</Badge>
                                            </td>
                                            <td>
                                                <small>{formatDateOnly(checkout.created_at)}</small>
                                            </td>
                                            <td>
                                                <small>{formatDateOnly(checkout.ict_checkout_date)}</small>
                                                {checkout.ict_checked_by && (
                                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {t('completedCheckoutsList.table.by')} {checkout.ict_checked_by}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <small>{formatDateOnly(checkout.security_checkout_date)}</small>
                                                {checkout.security_checked_by && (
                                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {t('completedCheckoutsList.table.by')} {checkout.security_checked_by}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <small>{formatDateOnly(checkout.hr_checkout_date)}</small>
                                                {checkout.hr_checked_by && (
                                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {t('completedCheckoutsList.table.by')} {checkout.hr_checked_by}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <Badge bg="success">
                                                    {formatDate(checkout.checkout_completion_date)}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge bg={durationDays <= 3 ? 'success' : durationDays <= 7 ? 'warning' : 'danger'}>
                                                    {durationDays} {durationDays === 1 ? t('completedCheckoutsList.duration.day') : t('completedCheckoutsList.duration.days')}
                                                </Badge>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Pagination info */}
                    {filteredCheckouts.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <small className="text-muted">
                                {t('completedCheckoutsList.pagination.showingOf', { count: filteredCheckouts.length, total: completedCheckouts.length })}
                            </small>
                            <small className="text-muted">
                                {t('completedCheckoutsList.table.clickToSort')}
                            </small>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default CompletedCheckoutsList;